import { Linkedin, Upload, Globe, Briefcase } from "lucide-react";

/**
 * Resolves the candidate's origin (where did they come from?) for display.
 *
 * Priority:
 *  1. Explicit external `utm_source` from the public application redirect
 *     (Catho, InfoJobs, Vagas.com, etc — anything the recruiter set up as
 *     a UTM channel). Best signal because it identifies the EXACT board.
 *  2. `source === "linkedin"` — promoted from a LinkedIn lead (post engagement)
 *  3. `source === "public_application"` — applied directly via the public
 *     career page, no UTM (likely shared link by WhatsApp/email)
 *  4. `source === "upload"` or empty — recruiter manually uploaded the PDF
 *     inside GridRecruiter
 *
 * Returns `null` if no useful origin can be inferred.
 */
export function getCandidateOrigin(c) {
  if (!c) return null;

  const utm = (c.utm_source || "").trim().toLowerCase();
  const source = (c.source || "").trim().toLowerCase();

  // External job board / referral via UTM (Catho, InfoJobs, etc.)
  // These are recognized boards we want to show specifically.
  const KNOWN_BOARDS = {
    catho: { label: "Catho", color: "amber" },
    infojobs: { label: "InfoJobs", color: "blue" },
    vagas: { label: "Vagas.com", color: "orange" },
    indeed: { label: "Indeed", color: "blue" },
    gupy: { label: "Gupy", color: "violet" },
    glassdoor: { label: "Glassdoor", color: "emerald" },
    instagram: { label: "Instagram", color: "rose" },
    facebook: { label: "Facebook", color: "blue" },
  };
  if (utm && KNOWN_BOARDS[utm]) {
    return {
      label: KNOWN_BOARDS[utm].label,
      icon: Briefcase,
      color: KNOWN_BOARDS[utm].color,
      tooltip: `Candidatou-se via ${KNOWN_BOARDS[utm].label}${
        c.utm_medium ? ` (${c.utm_medium})` : ""
      }`,
    };
  }

  // LinkedIn (either promoted from a lead OR explicit UTM=linkedin)
  if (source === "linkedin" || utm === "linkedin") {
    return {
      label: "LinkedIn",
      icon: Linkedin,
      color: "blue",
      tooltip:
        source === "linkedin"
          ? "Promovido a candidato a partir de comentário/reação em post do LinkedIn"
          : "Candidatou-se via link compartilhado no LinkedIn",
    };
  }

  // Custom UTM that is not one of the known boards — show as-is, capitalized
  if (utm) {
    return {
      label: utm.charAt(0).toUpperCase() + utm.slice(1),
      icon: Briefcase,
      color: "slate",
      tooltip: `Candidatou-se via ${utm}${c.utm_medium ? ` (${c.utm_medium})` : ""}`,
    };
  }

  // Came via the public career page without UTM (shared link, direct visit)
  if (source === "public_application") {
    return {
      label: "Página pública",
      icon: Globe,
      color: "violet",
      tooltip: "Candidatou-se diretamente pela página pública da vaga",
    };
  }

  // Manual upload by the recruiter (default for legacy candidates too)
  if (source === "upload" || source === "") {
    return {
      label: "Upload CV",
      icon: Upload,
      color: "slate",
      tooltip: "Currículo enviado pelo recrutador no GridRecruiter",
    };
  }

  // Catch-all for new sources we don't know about yet
  return {
    label: source.charAt(0).toUpperCase() + source.slice(1),
    icon: Briefcase,
    color: "slate",
    tooltip: `Origem: ${source}`,
  };
}

const COLOR_CLASSES = {
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
};

// Hex equivalents for inline styles (chart bars, dots, etc.) — pick the 500
// shade to feel solid but not garish against a white background.
export const ORIGIN_COLOR_HEX = {
  amber: "#d97706",
  blue: "#2563eb",
  orange: "#ea580c",
  violet: "#7c3aed",
  emerald: "#059669",
  rose: "#e11d48",
  slate: "#64748b",
};

/**
 * Inline badge showing the candidate's origin near their name.
 * Stays neutral (small, no hover state — it's informational only).
 */
export function CandidateOriginBadge({ candidate, className = "" }) {
  const origin = getCandidateOrigin(candidate);
  if (!origin) return null;
  const Icon = origin.icon;
  const colorCls = COLOR_CLASSES[origin.color] || COLOR_CLASSES.slate;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium ${colorCls} ${className}`}
      data-testid={`candidate-origin-${candidate.id}`}
      data-origin={origin.label}
      title={origin.tooltip}
    >
      <Icon className="w-3 h-3" aria-hidden />
      <span>Origem: {origin.label}</span>
    </span>
  );
}
